import ErrorComponent from 'Components/ErrorComponent';
import VesselDetailsHeader from 'Components/VesselDetailsHeader';
import Layout from 'HorizontalMenu/Menu';
import React, { useEffect, useState } from 'react'
import { Navbar, Container, Card, CardHeader, Row, Col, CardFooter } from 'reactstrap';
import { VesselState, clearAlerts, getCurrentReportAction, getPreviousReportAction, setSelectedOperations, setVesselState } from "../Store/Generic/ReportingSlice";
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import { calculateGeometricDistance, calculateUTCTime, dateTimeFormat, handleServerResponse, dynamicSetFieldErrors, useSafeFormikContext, checkValidObject, checkValidArray } from 'GenericForms/Helper';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get, nextPossibleEvents, nextPossibleOperations } from 'VesselMaster/vesselMaster.hooks';
import { AlertColourTypeConstant, AlertTypeConstant, EventPrecedence, ReportingEvents, ReportingStatusConstant, ReportingTypeConstant, VesselTypeConstant } from 'shared/constants';
import { useFormik } from 'formik';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import * as Yup from 'yup';
import apiGlobal from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import env from 'environment_system/env_system';
import AlertPopup from 'Alerts/AlertPopup';
import { getAlertMessage } from 'Alerts/AlertMessages';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import ErrorReport from 'Components/ErrorReport';
import CreateVesselReportStructure from './EditCompletedReports/CreateVesselReportStructure';
import LoadingSkeletonForm from 'Components/LoadingPopUp';

const CreateVesselReport = () => {
    /** State variables */
    const dispatch = useDispatch();
    const { VesselState, Voyages, VoyageID, VesselID, CurrentVessel, CurrentReport, PreviousReport, ReportID } = useSelector((state: RootState) => state.Reporting);
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    const voyageParameterCurrentReport = CurrentReport?.data?.chief_engineer?.voyage_parameter ?? null;
    const voyage = Voyages.find((rec: any) => rec.id === VoyageID);
    const [reportId, setReportId] = useState<number>(ReportID);
    const [reportingType, setReportingType] = useState("");
    const [eventId, setEventId] = useState(0);
    const [count, setCount] = useState(0);
    const [refreshCheckboxes, setRefreshCheckboxes] = useState(0);
    const [alertBool, setAlertBool] = useState(false);
    const [alertColourType, setAlertColourType] = useState(null);
    const [alertType, setAlertType] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [alertFieldValue, setAlertFieldValue] = useState<any>(null);
    const [alertFieldLabel, setAlertFieldLabel] = useState<any>(null);
    const [sendNotification, setSendNotification] = useState(true);
    const [fieldId, setFieldId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);
    const [submitBool, setSubmitBool] = useState(false);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const [refreshDistance, setRefreshDistance] = useState(0);
    const [refreshFields, setRefreshFields] = useState(0);
    const [refreshFieldApplicable, setRefreshFieldApplicable] = useState(false);
    useDocumentTitle("Create Vessel Report - ecoSAIL");
    const bufferedAlerts = useSelector((state: RootState) => state.Reporting.alertsBuffer || []);
    /** State variables end */

    /** Set vessel globally */
    const handleVesselState = (record: VesselState) => {
        dispatch(setVesselState(record));
    };

    /** Queries start */
    /** Load reporting event options based on previous reporting event */
    const { data: reportingEvent, isLoading: reportingEventIsLoading, isError: reportingEventIsError } = useQuery(
        [queryKeyes.vessel.nextReportingEvent, eventId],
        async () => {
            if (eventId) {
                setCount(count + 1);
                return await nextPossibleEvents(eventId);
            }
            return { id: 0, event: "No Records found" };
        },
    );
    /** Load additional reporting events based on previous reporting event */
    const { data: additionalEvents, isLoading: additionalEventsIsLoading, isError: additionalEventsIsError } = useQuery(
        [queryKeyes.vessel.CIIAdjustmentAdditionalEvents, eventId],
        async () => {
            if (eventId) {
                setCount(count + 1);
                return await Get(queryKeyes.vessel.CIIAdjustmentAdditionalEvents.url(eventId), eventId);
            }
            return { id: 0, event: "No Records found" };
        },
        {
            staleTime: 0,
        }
    );
    /** Load current year object from master */
    const { data: currentYear, isLoading: currentYearLoading, isError: currentYearError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.CurrentYear.key],
            async () => {
                return await Get(queryKeyes.vessel.CurrentYear.url());
            },
            { staleTime: 0 }
        );
    /** Load load conditions from master */
    const { data: LoadConditionOptions, isLoading: LoadingLoadCondition, isError: ErrorLoadCondition } = useQuery(
        [queryKeyes.masters.LoadConditionMaster.key, VesselState],
        async () => await Get(queryKeyes.masters.LoadConditionMaster.url()),
        {
            enabled: true,
            staleTime: 0
        });
    /** Load load conditions from master */
    const { data: StraitCanals, isLoading: LoadingStraitCanal, isError: ErrorStraitCanal } = useQuery(
        [queryKeyes.masters.straitCanal.key, VesselState],
        async () => await Get(queryKeyes.masters.straitCanal.url()),
        {
            enabled: true,
            staleTime: 0
        });
    /** Vessel report object used for edit */
    const { data: MaxSpeedConsumption, isLoading: MaxSpeedConsumptionLoading, isError: MaxSpeedConsumptionError }:
        { data: any; isLoading: any; isError: any } = useQuery(
            [queryKeyes.vessel.MaxSpeedConsumptionByVessel.key, VesselID],
            async () => await Get(queryKeyes.vessel.MaxSpeedConsumptionByVessel.url(VesselID), VesselID),
            {
                enabled: true,
                staleTime: 0
            });
    /** Vessel's other setting;s object used for edit */
    const { data: OtherSettings, isLoading: OtherSettingsLoading, isError: OtherSettingsError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.VesselOtherSettingsObject.key, VesselID],
            async () => { return await Get(queryKeyes.vessel.VesselOtherSettingsObject.url(VesselID), VesselID) },
            {
                enabled: true,
                staleTime: Infinity,
            }
        );
    /** Queries end */

    /** Assign initial values to the formik object */
    const getInitialValues = () => {
        if (editInitialValues && editInitialValues?.vessel_reporting_information?.id > 0) {
            return editInitialValues?.vessel_reporting_information;
        } else if (checkValidObject(currentVesselReport)) {
            const updatedVesselReport = { ...currentVesselReport };
            updatedVesselReport.time_difference_prefix = currentVesselReport?.reporting_time_difference?.toString()?.substring(0, 1);
            return updatedVesselReport;
        } else {
            return {
                reporting_time_utc: "",
                time_duration_since_last_report: null,
                time_difference_prefix: null,
                reporting_time_difference: null,
                reporting_type: "",
                local_time: "",
                reporting_event: null,
                year: currentYear && currentYear?.id,
                reporting_operation_mode: null,
                reporting_operation_mode_name: "",
                is_drifting_involved: false,
                position_lat_deg: null as number,
                position_lat_min: null as number,
                position_lat_sec: null as number,
                position_long_deg: null as number,
                position_long_min: null as number,
                position_long_sec: null as number,
                lat_direction: null,
                long_direction: null,
                strait_canal_transit: null,
                no_of_hours_retarded: null,
                retarded_advanced: "retarded",
                remaining_distance_to_the_next_port: 0,
                distance_travelled: null,
                load_condition: null,
                is_noon_report: false,
                is_cii_adjustment_report: false,
                only_noon_or_combine_with_other_event: "none",
                cii_combine_with_other_event: false,
                cii_noon_combine_with_other_event: false,
                additional_events: false,
                is_strait_canal_transit: false,
                previous_event_other_than_noon: 0,
                previous_operation: previousVesselReport?.reporting_operation_mode,
                is_this_cargo_loading_discharging: null,
                change_in_containers_onboard: null,
                is_sts_considered: false,
                vessel: VesselID,
                voyage_information: VoyageID,
            };
        }
    };

    /** Formik object of CreateVesselReport */
    const CreateVesselReportFormik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: env?.form_validation ? Yup.object().shape({
            local_time: Yup.date()
                .required(commonValidationMessages.required)
                .typeError(commonValidationMessages.dateTime)
                .test(
                    'greater-than-last',
                    `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`,
                    value => !value || value > new Date(dateTimeFormat(previousVesselReport?.local_time))
                ),
            time_duration_since_last_report: Yup.number(),
            reporting_time_difference: Yup.string()
                .matches(/^(?:null\d{2}:\d{2}|[+-]?\d{2}:\d{2})$/, "Please enter a valid time difference")
                .required(commonValidationMessages.required),
            reporting_event: Yup.number().required(commonValidationMessages.required),
            reporting_operation_mode: Yup.number().required(commonValidationMessages.required),
            position_lat_deg: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minLatDegree)
                .max(90, commonValidationMessages.maxLatDegree)
                .required(commonValidationMessages.required),
            position_lat_min: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minMinutes)
                .max(60, commonValidationMessages.maxMinutes)
                .required(commonValidationMessages.required),
            position_lat_sec: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minSecond)
                .max(60, commonValidationMessages.maxSecond)
                .required(commonValidationMessages.required),
            position_long_deg: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minLogDegree)
                .max(180, commonValidationMessages.maxLogDegree)
                .required(commonValidationMessages.required),
            position_long_min: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minMinutes)
                .max(60, commonValidationMessages.maxMinutes)
                .required(commonValidationMessages.required),
            position_long_sec: Yup.number()
                .typeError(commonValidationMessages.onlynumbers)
                .min(0, commonValidationMessages.minSecond)
                .max(60, commonValidationMessages.maxSecond)
                .required(commonValidationMessages.required),
            lat_direction: Yup.string().required(commonValidationMessages.required),
            long_direction: Yup.string().required(commonValidationMessages.required),
            time_difference_prefix: Yup.string().required(commonValidationMessages.required),
            remaining_distance_to_the_next_port: Yup.string()
                .matches(/^\d{0,4}(\.\d{1,4})?$/, `${commonValidationMessages.before4after4}`)
                .nullable(),
            distance_travelled: Yup.string()
                .matches(/^\d{0,3}(?:\.\d{1,4})?$/, `${commonValidationMessages.before3after4}`)
                .when("reporting_event", {
                    is: (value: number) => value === ReportingEvents.DEPARTURE_FROM_BERTH || value === ReportingEvents.HEAVE_ANCHOR,
                    then: schema => schema.nullable(),
                    otherwise: schema => schema.required(commonValidationMessages.required),
                }),
            load_condition: Yup.number().required(commonValidationMessages.required),
            is_noon_report: Yup.boolean(),
            is_this_cargo_loading_discharging: Yup.string().when("$reporting_event", {
                is: (value: number) =>
                    value !== ReportingEvents.NOON_FWE_IP &&
                    value !== ReportingEvents.DEPARTURE_FROM_BERTH &&
                    CurrentVessel?.vessel_type_precedence_id !== VesselTypeConstant.CONTAINER,
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
            change_in_containers_onboard: Yup.string().when("$reporting_event", {
                is: (value: number) =>
                    value !== ReportingEvents.NOON_FWE_IP &&
                    value !== ReportingEvents.DEPARTURE_FROM_BERTH &&
                    OtherSettings?.[0]?.is_container_available === true,
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
            only_noon_or_combine_with_other_event: Yup.string(),
            is_strait_canal_transit: Yup.boolean(),
            strait_canal_transit: Yup.number().nullable().when('is_strait_canal_transit', {
                is: true,
                then: schema => schema.required(commonValidationMessages.required),
                otherwise: schema => schema.nullable(),
            }),
            vessel: Yup.number(),
            voyage_information: Yup.number(),
        }) : null,
        onSubmit: async (values, actions) => {
            if (env?.form_validation) {
                actions.setSubmitting(true);
                values.time_duration_since_last_report = calculateSteamingTime();
                values.reporting_time_utc = calculateUTCTime(
                    CreateVesselReportFormik?.values?.local_time,
                    CreateVesselReportFormik?.values?.reporting_time_difference,
                    CreateVesselReportFormik?.values?.id
                );

                // Include buffered alerts in payload
                if (bufferedAlerts.length > 0) {
                    values.alerts = [...bufferedAlerts];
                }

                const payload = submitBool
                    ? { ...values, vessel_reporting_status: ReportingStatusConstant.COMPLETED }
                    : values;
                const responseArray: any[] = [];
                if (checkValidObject(currentVesselReport) && reportId) {
                    responseArray.push(apiGlobal.put(`/vessel_reporting_information/${reportId}/`, payload));
                } else {
                    const res = await apiGlobal.post(`/vessel_reporting_information/`, payload);
                    setReportId(res?.data?.id);
                    setRefreshKey(refreshKey + 1);
                    responseArray.push(res);
                }
                await handleServerResponse(responseArray).then(async (res) => {
                    if (res === true) {
                        if (reportId > 0 && checkValidArray(voyageParameterCurrentReport) &&
                            (CreateVesselReportFormik?.initialValues?.time_duration_since_last_report !==
                                CreateVesselReportFormik?.values?.time_duration_since_last_report ||
                                CreateVesselReportFormik?.initialValues?.distance_travelled !==
                                CreateVesselReportFormik?.values?.distance_travelled)
                        ) {
                            voyageParameterCurrentReport.forEach(async (voyageParam: any) => {
                                voyageParam.slip_percentage = parseFloat(((100 *
                                    ((voyageParam?.avg_rpm * CreateVesselReportFormik?.values?.time_duration_since_last_report *
                                        voyageParam?.propeller_pitch) / 1852 - CreateVesselReportFormik?.values?.distance_travelled)) /
                                    ((voyageParam?.avg_rpm * CreateVesselReportFormik?.values?.time_duration_since_last_report *
                                        voyageParam?.propeller_pitch) / 1852))?.toFixed(2));
                                if (!voyageParam?.is_revolution_counter_not_functional) {
                                    voyageParam.avg_rpm = parseFloat((voyageParam?.total_revolutions / CreateVesselReportFormik?.values?.time_duration_since_last_report)?.toFixed(4));
                                } else {
                                    voyageParam.total_revolutions = parseFloat((voyageParam?.avg_rpm * CreateVesselReportFormik?.values?.time_duration_since_last_report)?.toFixed(2));
                                }
                                try {
                                    const vpRes = await apiGlobal.put(`${queryKeyes.vessel.VoyageParameter.url()}${voyageParam?.id}/`, voyageParam);
                                    if (vpRes.status === 200) {
                                        await queryClient.invalidateQueries(queryKeyes.vessel.VoyageParameterObject.key);
                                    }
                                } catch (err) {
                                    console.error(err);
                                }
                            });
                        }
                        dispatch(clearAlerts());
                        dispatch(setSelectedOperations([]));
                        dispatch(getCurrentReportAction(VesselID, reportId));
                        await queryClient.invalidateQueries(queryKeyes.pagination.completeReportPage.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.reporting.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.CurrentSteamingTime.key);
                        await queryClient.invalidateQueries(queryKeyes.vessel.CurrentDistanceTravelled.key);
                        await queryClient.invalidateQueries(queryKeyes.notification.NotificationInbox.key);
                        await queryClient.invalidateQueries(queryKeyes.notification.NotificationForLoggedInUser.key);
                        if (submitBool) {
                            handleVesselState("ONGOING_VESSEL_REPORTING");
                        }
                    } else {
                        setErrorMessage(res);
                        dynamicSetFieldErrors(res, actions, false);
                    }
                });
                setSubmitBool(false);
                actions.setSubmitting(false);
            }
        }
    });


    /** useEffect start */
    useEffect(() => {
        if (reportId > 0) {
            dispatch(getPreviousReportAction(VesselID, reportId));
            dispatch(getCurrentReportAction(VesselID, reportId));
        } else {
            dispatch(getPreviousReportAction(VesselID, reportId, true));
        }
    }, [VesselID, reportId, dispatch]);
    useEffect(() => {
        if (checkValidObject(previousVesselReport)) {
            if (checkValidObject(currentVesselReport)) {
                setEventId(currentVesselReport?.previous_event_other_than_noon);
            } else if (previousVesselReport?.event_precedence_id === EventPrecedence.NOON ||
                previousVesselReport.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT) {
                setEventId(previousVesselReport?.previous_event_other_than_noon);
            } else {
                setEventId(previousVesselReport?.reporting_event);
            }
        }
        setCount(count + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVesselReport, previousVesselReport]);

    useEffect(() => {
        if (editInitialValues) {
            setEditValues({
                ...editInitialValues,
                vessel_reporting_information: CreateVesselReportFormik?.values
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CreateVesselReportFormik?.values])
    useEffect(() => {
        CreateVesselReportFormik.initialValues = getInitialValues();
        CreateVesselReportFormik.values = getInitialValues();
        CreateVesselReportFormik.setFieldValue("previous_operation", previousVesselReport?.reporting_operation_mode);
        CreateVesselReportFormik.setFieldValue("time_difference_prefix", currentVesselReport?.reporting_time_difference?.toString().substring(0, 1))
        setRefreshKey((prevKey) => prevKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVesselReport, previousVesselReport]);
    useEffect(() => {
        calculateSteamingTime();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CreateVesselReportFormik?.values?.local_time, CreateVesselReportFormik?.values?.reporting_time_difference]);
    useEffect(() => {
        if (CurrentVessel?.vessel_type_precedence_id !== VesselTypeConstant.CONTAINER) {
            if (CreateVesselReportFormik?.values?.reporting_event !== null) {
                if (CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.NOON_FWE_IP ||
                    CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.DEPARTURE_FROM_BERTH) {
                    CreateVesselReportFormik?.setFieldValue("is_this_cargo_loading_discharging", "yes");
                }
            }
        } else {
            CreateVesselReportFormik?.setFieldValue("is_this_cargo_loading_discharging", null);
        }
        if (((CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER || CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP) &&
            OtherSettings && OtherSettings?.[0]?.is_container_available === true)) {
            if (CreateVesselReportFormik?.values?.reporting_event !== null) {
                if (CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.NOON_FWE_IP ||
                    CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.DEPARTURE_FROM_BERTH) {
                    CreateVesselReportFormik?.setFieldValue("change_in_containers_onboard", "yes");
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CreateVesselReportFormik?.values?.reporting_event]);
    useEffect(() => {
        if (CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.HEAVE_ANCHOR ||
            CreateVesselReportFormik?.values?.reporting_event === ReportingEvents.DEPARTURE_FROM_BERTH) {
            CreateVesselReportFormik?.setFieldValue("distance_travelled", CreateVesselReportFormik?.initialValues?.distance_travelled);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CreateVesselReportFormik?.values?.reporting_event]);
    /** useEffect end */

    const calculateSteamingTime = () => {
        const dt1 = new Date(previousVesselReport?.reporting_time_utc);
        const dt2 = new Date(`${calculateUTCTime(
            CreateVesselReportFormik?.values?.local_time,
            CreateVesselReportFormik?.values?.reporting_time_difference,
            CreateVesselReportFormik?.values?.id
        )?.toString()?.substring(0, 10)}T${calculateUTCTime(
            CreateVesselReportFormik?.values?.local_time,
            CreateVesselReportFormik?.values?.reporting_time_difference,
            CreateVesselReportFormik?.values?.id
        )?.toString()?.substring(11, 16)
            }:00Z`)
        let timeDifference = Math.floor(
            (Date.UTC(
                dt2.getFullYear(),
                dt2.getUTCMonth(),
                dt2.getUTCDate(),
                dt2.getUTCHours(),
                dt2.getUTCMinutes(),
                dt2.getUTCSeconds()
            ) -
                Date.UTC(
                    dt1.getFullYear(),
                    dt1.getUTCMonth(),
                    dt1.getUTCDate(),
                    dt1.getUTCHours(),
                    dt1.getUTCMinutes(),
                    dt1.getUTCSeconds()
                )) /
            (1000 * 60)
        );

        if (Number.isNaN(timeDifference) || timeDifference < 0) {
            if (editInitialValues && editInitialValues?.vessel_reporting_information?.id > 0) {
                CreateVesselReportFormik?.setFieldValue("time_duration_since_last_report",
                    editInitialValues?.vessel_reporting_information?.time_duration_since_last_report);
                return editInitialValues?.vessel_reporting_information?.time_duration_since_last_report;
            } else if (checkValidObject(currentVesselReport)) {
                CreateVesselReportFormik?.setFieldValue("time_duration_since_last_report",
                    currentVesselReport?.time_duration_since_last_report);
                return currentVesselReport?.time_duration_since_last_report;
            }
        }
        CreateVesselReportFormik?.setFieldValue("time_duration_since_last_report", timeDifference);
        return timeDifference;
    }

    /** Calculate steaming time */
    const handleReportingDateTimeChanges = (e: any, generateAlert: boolean) => {
        const { name, value } = e.target;
        const utcTime = calculateUTCTime(CreateVesselReportFormik?.values?.local_time, CreateVesselReportFormik?.values?.reporting_time_difference, CreateVesselReportFormik?.values.id);
        CreateVesselReportFormik.setFieldValue("reporting_time_utc", utcTime);
        const steamingMinutes = calculateSteamingTime();
        if (!env?.alerts || !generateAlert || steamingMinutes <= 1500) return;
        if (alertBool) return;
        const elapsedHours = (steamingMinutes / 60).toFixed(1);
        const fieldLabel =
            name === "local_time"
                ? "Reporting Date & Time (Local)"
                : "Difference between Local Time & UTC";
        setAlertMessage(
            getAlertMessage(
                "VesselReporting",
                "reporting_time_utc",
                fieldLabel,
                `${elapsedHours} hours`,
                "25 hours"
            )
        );
        setAlertColourType(AlertColourTypeConstant.WARNING);
        setAlertType(AlertTypeConstant.WITH_REASON);
        setFieldId(name);
        setAlertBool(true);
        setAlertFieldLabel(fieldLabel);
        setAlertFieldValue(value);
    };

    /** Verify if the vessel has advanced or retarded */
    const verifyAdvancedRetarded = () => {
        if (parseFloat(previousVesselReport?.reporting_time_difference) < parseFloat(CreateVesselReportFormik?.values?.reporting_time_difference)) {
            return "advanced";
        } else {
            return "retarded";
        }
    }

    /** Calculate number of hours retarded/advanced */
    const handleTimeDifferenceTextboxChanges = (e: any, isTimeDifference: boolean) => {
        CreateVesselReportFormik?.setFieldValue("reporting_time_utc",
            calculateUTCTime(
                CreateVesselReportFormik?.values?.local_time,
                CreateVesselReportFormik?.values?.reporting_time_difference,
                CreateVesselReportFormik?.values?.id
            )
        );
        if (CreateVesselReportFormik?.values.time_difference_prefix === undefined) {
            CreateVesselReportFormik?.setFieldValue("time_difference_prefix", "-");
        }
        if (isTimeDifference) {
            CreateVesselReportFormik?.setFieldValue(
                "reporting_time_difference",
                `${CreateVesselReportFormik?.values.time_difference_prefix}${e.target.value}`
            );
        }

        /** Calculation of number of hours retarded/advanced */
        let lastReportingTimeDifference: any;
        let currentReportingTimeDifference: any;
        if (
            previousVesselReport?.reporting_time_difference === "-00:00" ||
            previousVesselReport?.reporting_time_difference === "+00.00"
        ) {
            lastReportingTimeDifference = 0;
        } else {
            if (previousVesselReport) {
                lastReportingTimeDifference =
                    parseInt(
                        previousVesselReport?.reporting_time_difference?.toString().substring(1, 3)
                    ) +
                    parseInt(
                        previousVesselReport?.reporting_time_difference?.toString().substring(4, 6)
                    ) /
                    60;
            }
        }
        if (isTimeDifference) {
            currentReportingTimeDifference =
                parseInt(e.target.value.toString().substring(0, 2)) +
                parseInt(e.target.value.toString().substring(3, 5)) / 60;
        } else {
            currentReportingTimeDifference =
                parseInt(CreateVesselReportFormik?.values?.reporting_time_difference?.substring(1, 3)) +
                parseInt(CreateVesselReportFormik?.values?.reporting_time_difference?.substring(4, 6)) / 60;
        }
        let timeDifference =
            parseFloat(
                `${isTimeDifference ? CreateVesselReportFormik?.values?.time_difference_prefix : (e.target.checked === true ? "+" : "-")}${currentReportingTimeDifference}`
            ) -
            parseFloat(
                `${previousVesselReport?.reporting_time_difference?.toString().substring(0, 1)}${lastReportingTimeDifference}`
            );
        if (!Number.isNaN(timeDifference)) {
            if (timeDifference > 0) {
                CreateVesselReportFormik?.setFieldValue(
                    "no_of_hours_retarded", timeDifference.toFixed(2)
                );
            } else if (timeDifference < 0) {
                CreateVesselReportFormik?.setFieldValue("no_of_hours_retarded", timeDifference.toFixed(2));
            } else {
                CreateVesselReportFormik?.setFieldValue("no_of_hours_retarded", parseInt(`+${0}`));
            }
            CreateVesselReportFormik?.setFieldValue("retarded_advanced", verifyAdvancedRetarded());
        }
    };

    /** Handle time difference(retarding/advancing) */
    const handleTimePrefixSwitchChanges = (e: any) => {
        let sign = e.target.id === "plus" ? "+" : "-";
        CreateVesselReportFormik?.setFieldValue("reporting_time_utc",
            calculateUTCTime(
                CreateVesselReportFormik?.values?.local_time,
                `${sign}${CreateVesselReportFormik?.values?.reporting_time_difference?.toString()?.substring(CreateVesselReportFormik?.values?.reporting_time_difference?.includes("null") ? 4 : 1, CreateVesselReportFormik?.values?.reporting_time_difference?.includes("null") ? 9 : 6)}`,
                CreateVesselReportFormik?.values?.id
            )
        );
        CreateVesselReportFormik?.setFieldValue("time_difference_prefix", sign);
        if (CreateVesselReportFormik?.values.reporting_time_difference !== null) {
            CreateVesselReportFormik?.setFieldValue(
                "reporting_time_difference",
                `${sign}${CreateVesselReportFormik?.values?.reporting_time_difference?.toString()?.substring(CreateVesselReportFormik?.values?.reporting_time_difference?.includes("null") ? 4 : 1, CreateVesselReportFormik?.values?.reporting_time_difference?.includes("null") ? 9 : 6)}`
            );
        }
    };

    /** Set current operation & previous operation  */
    const loadNextPossibleOperations = async (currEvent: any) => {
        try {
            if (previousVesselReport.event_precedence_id === EventPrecedence.NOON ||
                previousVesselReport.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT) {
                CreateVesselReportFormik?.setFieldValue("reporting_operation_mode", previousVesselReport.reporting_operation_mode);
                CreateVesselReportFormik?.setFieldValue("reporting_operation_mode_name", previousVesselReport.reporting_operation_mode_name);
                CreateVesselReportFormik?.setFieldValue("previous_operation", previousVesselReport.reporting_operation_mode);
            } else {
                const nextPossibleOperation = await nextPossibleOperations(currEvent, previousVesselReport.reporting_event);
                CreateVesselReportFormik?.setFieldValue("reporting_operation_mode", nextPossibleOperation?.[0]?.possible_operations_slr);
                CreateVesselReportFormik?.setFieldValue("reporting_operation_mode_name", nextPossibleOperation?.[0]?.possible_operations_slr_name);
                if (
                    CreateVesselReportFormik?.values.only_noon_or_combine_with_other_event === "combine_with_other_report" ||
                    CreateVesselReportFormik?.values.is_noon_report === false
                ) {
                    CreateVesselReportFormik?.setFieldValue("previous_operation", nextPossibleOperation?.[0].possible_operations_slr);
                } else {
                    CreateVesselReportFormik?.setFieldValue("previous_operation", previousVesselReport.reporting_operation_mode);
                }
                return nextPossibleOperation?.[0];
            }
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    /** Changes based on whether the report is noon, not noon or combined */
    const handleIsNoonReportSwitchChanges = (e: any) => {
        CreateVesselReportFormik?.setFieldValue("is_noon_report", e.target.checked);
        CreateVesselReportFormik?.setFieldValue(
            "only_noon_or_combine_with_other_event",
            e.target.checked === true && CreateVesselReportFormik?.values?.is_cii_adjustment_report === false
                ? "only_noon_report" :
                e.target.checked === true && CreateVesselReportFormik?.values?.is_cii_adjustment_report === true ?
                    "combine_with_other_report" : "none"
        );
        if (e.target.checked === false) {
            CreateVesselReportFormik?.setFieldValue("cii_noon_combine_with_other_event", false);
        } else {
            CreateVesselReportFormik?.setFieldValue("cii_combine_with_other_event", false);
        }
        if ((e.target.checked === true && CreateVesselReportFormik?.values?.is_cii_adjustment_report === false) ||
            (e.target.checked === true && CreateVesselReportFormik?.values?.is_cii_adjustment_report === true &&
                CreateVesselReportFormik?.values?.cii_noon_combine_with_other_event === false
            )) {
            CreateVesselReportFormik?.setFieldValue(
                "reporting_event",
                reportingEvent?.[0]?.next_possible_events?.next_possible_events
                    .filter((item: any) => item?.event_precedence_id === EventPrecedence.NOON)?.[0]?.id
            );
            loadNextPossibleOperations(reportingEvent?.[0]?.next_possible_events?.next_possible_events
                .filter((item: any) => item?.event_precedence_id === EventPrecedence.NOON)?.[0]?.id);
        } else if (
            (e.target.checked === false &&
                CreateVesselReportFormik?.values?.is_cii_adjustment_report === true &&
                CreateVesselReportFormik?.values?.cii_combine_with_other_event === false)
        ) {
            CreateVesselReportFormik?.setFieldValue(
                "reporting_event",
                reportingEvent[0]?.next_possible_events?.next_possible_events
                    .filter((item: any) => item?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT)?.[0]?.id
            );
            loadNextPossibleOperations(reportingEvent[0]?.next_possible_events?.next_possible_events
                .filter((item: any) => item?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT)?.[0]?.id);
        } else if (e.target.checked === false && CreateVesselReportFormik?.values?.is_cii_adjustment_report === false) {
            CreateVesselReportFormik?.setFieldValue("reporting_event", null);
            CreateVesselReportFormik?.setFieldValue("reporting_event_name", null);
        }
        setReportingType(
            reportingEvent?.[0]?.next_possible_events?.next_possible_events
                .filter((item: any) => item?.event_precedence_id === EventPrecedence.NOON)?.[0]?.reporting_type_name
        );
        if (
            previousVesselReport?.event_precedence_id === EventPrecedence.NOON ||
            previousVesselReport?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT
        ) {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport?.previous_event_other_than_noon);
        } else {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport?.reporting_event);
        }
        setRefreshCheckboxes(refreshCheckboxes + 1);
        setCount(count + 1);
    };

    /** Changes based on whether this report is the beginning of CII adjustment event  */
    const handleIsCIIReportSwitchChanges = (e: any) => {
        CreateVesselReportFormik?.setFieldValue("is_cii_adjustment_report", e.target.checked);
        if (e.target.checked === false) {
            CreateVesselReportFormik?.setFieldValue("cii_combine_with_other_event", false);
            CreateVesselReportFormik?.setFieldValue("cii_noon_combine_with_other_event", false);
        }
        if (e.target.checked === true && CreateVesselReportFormik?.values.is_noon_report === true) {
            CreateVesselReportFormik?.setFieldValue("only_noon_or_combine_with_other_event", "combine_with_other_report");
            CreateVesselReportFormik?.setFieldValue(
                "reporting_event",
                reportingEvent?.[0]?.next_possible_events?.next_possible_events
                    .filter((item: any) => item?.event_precedence_id === EventPrecedence.NOON)?.[0]?.id
            );
        } else if (e.target.checked === true &&
            CreateVesselReportFormik?.values.cii_combine_with_other_event === false &&
            CreateVesselReportFormik?.values?.is_noon_report === false) {
            CreateVesselReportFormik?.setFieldValue(
                "reporting_event",
                reportingEvent?.[0]?.next_possible_events?.next_possible_events
                    .filter((item: any) => item?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT)?.[0]?.id
            );
            CreateVesselReportFormik?.setFieldValue("reporting_operation_mode", previousVesselReport.reporting_operation_mode);
            CreateVesselReportFormik?.setFieldValue("reporting_operation_mode_name", previousVesselReport.reporting_operation_mode_name);
            setReportingType(previousVesselReport.reporting_type);
        } else if (e.target.checked === false && CreateVesselReportFormik?.values.is_noon_report === false) {
            CreateVesselReportFormik?.setFieldValue("reporting_event", null);
            CreateVesselReportFormik?.setFieldValue("reporting_event_name", null);
        }
        if (previousVesselReport?.event_precedence_id === EventPrecedence.NOON || previousVesselReport?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT) {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport.previous_event_other_than_noon);
        } else {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport.reporting_event);
        }

        setRefreshCheckboxes(refreshCheckboxes + 1);
    };

    /** Changes based on which event is select */
    const handleReportingEventDropdownChanges = (e: any) => {
        if (reportingEvent && reportingEvent?.[0]?.next_possible_events?.next_possible_events
            .filter((item: any) => item.event_precedence_id !== EventPrecedence.CII_ADJUSTMENT_EVENT &&
                item.event_precedence_id !== EventPrecedence.NOON)?.length > 1 && env?.alerts) {
            setAlertColourType(AlertColourTypeConstant.INFO);
            setAlertType(AlertTypeConstant.WITH_YES_NO_ACTION);
            setFieldId('reporting_event');
            setAlertMessage(
                <React.Fragment>
                    <p>Please confirm that the option you have selected as the next reporting event is the correct one, as one of the listed events could also be applicable. If confirmed, kindly proceed by clicking <b>Yes</b>.</p>
                    {reportingEvent?.[0]?.next_possible_events?.next_possible_events
                        .filter((item: any) => item.event_precedence_id !== EventPrecedence.CII_ADJUSTMENT_EVENT &&
                            item.event_precedence_id !== EventPrecedence.NOON && e.id !== item.id)?.map((event: any) => {
                                return (
                                    <ul>
                                        <li className='text-start'>{event.event}</li>
                                    </ul>
                                )
                            })
                    }
                </React.Fragment>
            );
            setAlertBool(true);
            setAlertFieldLabel("Reporting Event");
            setAlertFieldValue(e.event);
            setSendNotification(false);
        }
        if (e.id === ReportingEvents.HEAVE_ANCHOR || e.id === ReportingEvents.DEPARTURE_FROM_BERTH) {
            CreateVesselReportFormik?.setFieldValue("distance_travelled", null);
            setRefreshDistance(refreshDistance + 1);
        }
        if (e.id === ReportingEvents.DEPARTURE_FROM_BERTH) {
            if (CurrentVessel?.vessel_type_precedence_id !== VesselTypeConstant.CONTAINER) {
                CreateVesselReportFormik?.setFieldValue("is_this_cargo_loading_discharging", "yes");
            } else if (
                (CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ||
                    CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP) &&
                OtherSettings && OtherSettings?.[0]?.is_container_available === true
            ) {
                CreateVesselReportFormik?.setFieldValue("change_in_containers_onboard", "yes");
            }
        }
        if (CurrentVessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER) {
            CreateVesselReportFormik?.setFieldValue("is_this_cargo_loading_discharging", null);
        }
        if (e?.last_operation_name && e?.last_operation_id) {
            CreateVesselReportFormik?.setFieldValue("reporting_operation_mode", e?.last_operation_id);
            CreateVesselReportFormik?.setFieldValue("reporting_operation_mode_name", e?.last_operation_name);
        }
        CreateVesselReportFormik?.setFieldValue("reporting_event", e.id);
        loadNextPossibleOperations(e.id);
        if (CreateVesselReportFormik?.values?.additional_events === false) {
            let indx = reportingEvent?.[0]?.next_possible_events?.next_possible_events?.indexOf(e);
            setReportingType(reportingEvent?.[0]?.next_possible_events?.next_possible_events[indx]?.reporting_type_name);
            if (reportingEvent?.[0]?.next_possible_events?.next_possible_events[indx]?.reporting_type_name === ReportingTypeConstant.INPORT) {
                CreateVesselReportFormik?.setFieldValue("remaining_distance_to_the_next_port", null);
            }
        } else {
            let indx = additionalEvents?.[0]?.next_possible_events?.next_possible_events?.indexOf(e);
            setReportingType(additionalEvents?.[0]?.next_possible_events?.next_possible_events[indx]?.reporting_type_name);
            if (reportingEvent?.[0]?.next_possible_events?.next_possible_events[indx]?.reporting_type_name === ReportingTypeConstant.INPORT) {
                CreateVesselReportFormik?.setFieldValue("remaining_distance_to_the_next_port", null);
            }
        }
        if (previousVesselReport.event_precedence_id === EventPrecedence.NOON || previousVesselReport?.event_precedence_id === EventPrecedence.CII_ADJUSTMENT_EVENT) {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport.previous_event_other_than_noon);
        } else {
            CreateVesselReportFormik?.setFieldValue("previous_event_other_than_noon", previousVesselReport.reporting_event);
        }
    };

    const generateDistanceAlert = (distance: string) => {
        const distanceValue = parseFloat(distance);
        const geometricDistance = calculateGeometricDistance(previousVesselReport, CreateVesselReportFormik?.values);
        const speed = parseFloat(MaxSpeedConsumption?.speed);
        const timeDuration = Number(CreateVesselReportFormik?.values?.time_duration_since_last_report);

        // ⚠️ Condition 1: Distance is less than geometric distance (based on coordinates)
        if (env?.alerts && distanceValue < geometricDistance) {
            setAlertColourType(AlertColourTypeConstant.WARNING);
            setAlertType(AlertTypeConstant.INFO_ONLY);
            setAlertMessage(
                getAlertMessage(
                    "VesselReporting",
                    "distance_travelled_position_coordindates",
                    "Distance travelled",
                    distanceValue.toFixed(2),
                    geometricDistance.toFixed(2)
                )
            );
            setAlertBool(true);
            setAlertFieldLabel("Distance Travelled");
            setAlertFieldValue(distanceValue.toFixed(2));
        }

        // Calculate theoretical max distance based on time and speed
        let calcDistance: number | null = null;
        if (distance !== "" && speed) {
            calcDistance = (timeDuration / 60) * speed;
        }

        // ⚠️ Condition 2: Entered distance > possible distance from time × speed
        if (calcDistance && env?.alerts && calcDistance < distanceValue) {
            setAlertColourType(AlertColourTypeConstant.WARNING);
            setAlertType(AlertTypeConstant.WITH_REASON);
            setFieldId("distance_travelled");
            setAlertMessage(
                getAlertMessage(
                    "VesselReporting",
                    "distance_travelled_speed_time",
                    "Distance travelled",
                    distanceValue.toFixed(2),
                    calcDistance.toFixed(2)
                )
            );
            setAlertBool(true);
            setAlertFieldLabel("Distance Travelled");
            setAlertFieldValue(distanceValue.toFixed(2));
        }
    };

    const setReportingEventNull = () => {
        CreateVesselReportFormik.setFieldValue("reporting_event", null);
        CreateVesselReportFormik.setFieldValue("reporting_event_name", null);
    }
    return (
        <React.Fragment>
            {(VesselState === "CREATE_VESSEL_REPORTING" ||
                VesselState === "EDIT") ?
                <React.Fragment>
                    <Layout>
                        <Navbar />
                    </Layout>
                    <div className="page-content">
                        <Container fluid>
                            <Card>
                                <CardHeader className="pt-0 pb-0">
                                    <Row>
                                        <Col sm={2} className="d-flex align-items-center">
                                            <button
                                                color="primary"
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    handleVesselState("ONGOING_VESSEL_REPORTING");
                                                    dispatch(clearAlerts());
                                                }}
                                            >
                                                <i className="bx bx-chevron-left me-1" />
                                                Back
                                            </button>
                                        </Col>
                                        <Col sm={10}>
                                            <VesselDetailsHeader />
                                        </Col>
                                    </Row>
                                </CardHeader>
                                {(
                                    reportingEventIsLoading ||
                                    additionalEventsIsLoading ||
                                    currentYearLoading ||
                                    LoadingLoadCondition ||
                                    LoadingStraitCanal ||
                                    MaxSpeedConsumptionLoading ||
                                    OtherSettingsLoading
                                ) && (
                                        <LoadingSkeletonForm
                                            layout={[
                                                [100, 100, 100],
                                                [120, 100, 100],
                                                [100, 100, 100],
                                                [100, 100, 100],
                                                [120, 100, 100],
                                                [100, 100, 100],
                                            ]}
                                        />)}
                                {(
                                    reportingEventIsError ||
                                    additionalEventsIsError ||
                                    currentYearError ||
                                    ErrorLoadCondition ||
                                    ErrorStraitCanal ||
                                    MaxSpeedConsumptionError ||
                                    OtherSettingsError
                                ) && <ErrorComponent message="Unable to load required data!" />}
                                {!(
                                    reportingEventIsLoading ||
                                    additionalEventsIsLoading ||
                                    currentYearLoading ||
                                    LoadingLoadCondition ||
                                    LoadingStraitCanal ||
                                    MaxSpeedConsumptionLoading ||
                                    OtherSettingsLoading
                                ) &&
                                    !(
                                        reportingEventIsError ||
                                        additionalEventsIsError ||
                                        currentYearError ||
                                        ErrorLoadCondition ||
                                        ErrorStraitCanal ||
                                        MaxSpeedConsumptionError ||
                                        OtherSettingsError
                                    ) &&
                                    <React.Fragment>
                                        <CreateVesselReportStructure
                                            CreateVesselReportFormik={CreateVesselReportFormik}
                                            refreshKey={refreshKey}
                                            voyage={voyage}
                                            alertBool={alertBool}
                                            setAlertBool={setAlertBool}
                                            handleReportingDateTimeChanges={handleReportingDateTimeChanges}
                                            handleTimePrefixSwitchChanges={handleTimePrefixSwitchChanges}
                                            handleTimeDifferenceTextboxChanges={handleTimeDifferenceTextboxChanges}
                                            verifyAdvancedRetarded={verifyAdvancedRetarded}
                                            handleIsCIIReportSwitchChanges={handleIsCIIReportSwitchChanges}
                                            refreshCheckboxes={refreshCheckboxes}
                                            handleIsNoonReportSwitchChanges={handleIsNoonReportSwitchChanges}
                                            setCount={setCount}
                                            reportingEvent={reportingEvent}
                                            reportingType={reportingType}
                                            additionalEvents={additionalEvents}
                                            handleReportingEventDropdownChanges={handleReportingEventDropdownChanges}
                                            generateDistanceAlert={generateDistanceAlert}
                                            setAlertMessage={setAlertMessage}
                                            setAlertColourType={setAlertColourType}
                                            setAlertType={setAlertType}
                                            setFieldId={setFieldId}
                                            LoadConditionOptions={LoadConditionOptions}
                                            StraitCanals={StraitCanals}
                                            setRefreshKey={setRefreshKey}
                                            count={count}
                                            submitBool={submitBool}
                                            setSubmitBool={setSubmitBool}
                                            OtherSettings={OtherSettings}
                                            editValues={editValues}
                                            setEditValues={setEditValues}
                                            editInitalValues={editInitialValues}
                                            editSubmit={editSubmit}
                                            editIsSubmitting={editIsSubmitting}
                                            setAlertFieldLabel={setAlertFieldLabel}
                                            setAlertFieldValue={setAlertFieldValue}
                                            refreshDistance={refreshDistance}
                                            refreshFields={refreshFields}
                                            setRefreshFieldApplicable={setRefreshFieldApplicable}
                                        />
                                        {errorMessage && env?.form_validation &&
                                            <CardFooter>
                                                <ErrorReport
                                                    errorMessages={errorMessage}
                                                    ui_url={window.location.pathname}
                                                    setErrorMessage={setErrorMessage} />
                                            </CardFooter>
                                        }
                                    </React.Fragment>
                                }
                            </Card>
                        </Container>
                    </div>
                    {alertBool &&
                        <AlertPopup
                            state={alertBool}
                            setState={setAlertBool}
                            alertMessage={alertMessage}
                            setAlertMessage={setAlertMessage}
                            AlertColourType={alertColourType}
                            setAlertColourType={setAlertColourType}
                            alertType={alertType}
                            setAlertType={setAlertType}
                            formTitle='Vessel Reporting Information'
                            fieldId={fieldId}
                            setFieldNull={fieldId === 'reporting_event' && refreshFieldApplicable ? setReportingEventNull : null}
                            alertSize='md'
                            fieldLabel={alertFieldLabel}
                            fieldValue={alertFieldValue}
                            sendNotification={sendNotification}
                            setRefreshKey={setCount}
                            setRefreshFields={setRefreshFields}
                            refreshFieldApplicable={refreshFieldApplicable}
                            setRefreshFieldApplicable={setRefreshFieldApplicable}
                            isCreateVesselReport={true}
                        />
                    }
                </React.Fragment> :
                <CreateVesselReportStructure
                    CreateVesselReportFormik={CreateVesselReportFormik}
                    refreshKey={refreshKey}
                    voyage={voyage}
                    alertBool={alertBool}
                    setAlertBool={setAlertBool}
                    handleReportingDateTimeChanges={handleReportingDateTimeChanges}
                    handleTimePrefixSwitchChanges={handleTimePrefixSwitchChanges}
                    handleTimeDifferenceTextboxChanges={handleTimeDifferenceTextboxChanges}
                    verifyAdvancedRetarded={verifyAdvancedRetarded}
                    handleIsCIIReportSwitchChanges={handleIsCIIReportSwitchChanges}
                    refreshCheckboxes={refreshCheckboxes}
                    handleIsNoonReportSwitchChanges={handleIsNoonReportSwitchChanges}
                    setCount={setCount}
                    reportingEvent={reportingEvent}
                    reportingType={reportingType}
                    additionalEvents={additionalEvents}
                    handleReportingEventDropdownChanges={handleReportingEventDropdownChanges}
                    generateDistanceAlert={generateDistanceAlert}
                    setAlertMessage={setAlertMessage}
                    setAlertColourType={setAlertColourType}
                    setAlertType={setAlertType}
                    setFieldId={setFieldId}
                    LoadConditionOptions={LoadConditionOptions}
                    StraitCanals={StraitCanals}
                    setRefreshKey={setRefreshKey}
                    count={count}
                    submitBool={submitBool}
                    setSubmitBool={setSubmitBool}
                    OtherSettings={OtherSettings}
                    editValues={editValues}
                    setEditValues={setEditValues}
                    editInitalValues={editInitialValues}
                    editSubmit={editSubmit}
                    editIsSubmitting={editIsSubmitting}
                    setAlertFieldLabel={setAlertFieldLabel}
                    setAlertFieldValue={setAlertFieldValue}
                    refreshDistance={refreshDistance}
                />
            }
        </React.Fragment>
    )
}
export default CreateVesselReport;
